import React, { useState, useEffect } from "react";
import { getIdeas, getChannels, createIdea, updateIdea, deleteIdea, convertIdea } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash, ArrowRight, Lightbulb, Check, X } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

const SOURCES = ["trending", "audience_request", "series", "competitor", "seasonal", "other"];
const STATUSES = ["new", "approved", "rejected", "converted"];

export default function Ideas() {
  const { hasPermission } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [editingIdea, setEditingIdea] = useState(null);
  const [deletingIdea, setDeletingIdea] = useState(null);
  const [convertingIdea, setConvertingIdea] = useState(null);
  const [filterChannel, setFilterChannel] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [quickAdd, setQuickAdd] = useState("");
  const [formData, setFormData] = useState({ title: "", channel_id: "", description: "", source: "other", status: "new" });

  const fetchData = async () => {
    try {
      const [ideasRes, channelsRes] = await Promise.all([getIdeas(), getChannels()]);
      setIdeas(ideasRes.data);
      setChannels(channelsRes.data);
    } catch (err) {
      toast.error("Failed to load ideas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickAdd.trim()) return;
    try {
      await createIdea({ title: quickAdd.trim() });
      toast.success("Idea added");
      setQuickAdd("");
      fetchData();
    } catch (err) {
      toast.error("Failed to add idea");
    }
  };

  const handleOpenCreate = () => {
    setEditingIdea(null);
    setFormData({ title: "", channel_id: "", description: "", source: "other", status: "new" });
    setDialogOpen(true);
  };

  const handleOpenEdit = (idea) => {
    setEditingIdea(idea);
    setFormData({ title: idea.title, channel_id: idea.channel_id || "", description: idea.description || "", source: idea.source || "other", status: idea.status });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingIdea) {
        await updateIdea(editingIdea.id, formData);
        toast.success("Idea updated");
      } else {
        await createIdea(formData);
        toast.success("Idea created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save idea");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteIdea(deletingIdea.id);
      toast.success("Idea deleted");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete idea");
    }
  };

  const handleConvert = async () => {
    try {
      await convertIdea(convertingIdea.id);
      toast.success("Idea converted to video!");
      setConvertDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert idea");
    }
  };

  const handleQuickAction = async (idea, action) => {
    try {
      await updateIdea(idea.id, { status: action });
      toast.success(`Idea ${action}`);
      fetchData();
    } catch (err) {
      toast.error("Failed to update idea");
    }
  };

  const filteredIdeas = ideas.filter((i) => {
    if (filterChannel && i.channel_id !== filterChannel) return false;
    if (filterStatus && i.status !== filterStatus) return false;
    return true;
  });

  const canCreate = hasPermission("create_ideas");
  const canEdit = hasPermission("edit_ideas");

  if (loading) {
    return <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="surface-card p-6"><div className="skeleton h-20 w-full" /></div>)}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white">Ideas Bank</h1>
          <p className="text-gray-400 text-sm">{ideas.length} ideas</p>
        </div>
        {canCreate && (
          <Button onClick={handleOpenCreate} className="btn-primary" data-testid="create-idea-btn">
            <Plus size={18} className="mr-2" />New Idea
          </Button>
        )}
      </div>

      {/* Quick Add */}
      {canCreate && (
        <form onSubmit={handleQuickAdd} className="flex gap-3">
          <Input value={quickAdd} onChange={(e) => setQuickAdd(e.target.value)} className="flex-1 bg-white/5 border-white/10 text-white" placeholder="Quick add idea... (press Enter)" data-testid="quick-add-idea" />
          <Button type="submit" className="btn-secondary" disabled={!quickAdd.trim()}>
            <Plus size={18} />
          </Button>
        </form>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={filterChannel} onValueChange={(v) => setFilterChannel(v === "all" ? "" : v)}>
          <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white"><SelectValue placeholder="All Channels" /></SelectTrigger>
          <SelectContent className="bg-[#1A1A2E] border-white/10">
            <SelectItem value="all" className="text-white">All Channels</SelectItem>
            {channels.map((c) => <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent className="bg-[#1A1A2E] border-white/10">
            <SelectItem value="all" className="text-white">All Status</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-gray-400">{filteredIdeas.length} ideas</span>
      </div>

      {/* Ideas List */}
      <div className="space-y-3">
        {filteredIdeas.map((idea) => (
          <div key={idea.id} className="surface-card p-4 hover:border-white/10 transition-all group" data-testid={`idea-card-${idea.id}`}>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-medium">{idea.title}</h3>
                  <span className={`badge ${
                    idea.status === "approved" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                    idea.status === "rejected" ? "bg-red-500/20 text-red-400 border-red-500/30" :
                    idea.status === "converted" ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
                    "bg-gray-500/20 text-gray-400 border-gray-500/30"
                  }`}>{idea.status}</span>
                </div>
                {idea.description && <p className="text-sm text-gray-400 line-clamp-1">{idea.description}</p>}
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  {idea.channel_name && <span>{idea.channel_name}</span>}
                  <span className="capitalize">{idea.source?.replace("_", " ")}</span>
                </div>
              </div>
              
              {canEdit && idea.status !== "converted" && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {idea.status === "new" && (
                    <>
                      <button onClick={() => handleQuickAction(idea, "approved")} className="p-2 rounded-lg hover:bg-green-500/10 text-gray-400 hover:text-green-400" title="Approve"><Check size={16} /></button>
                      <button onClick={() => handleQuickAction(idea, "rejected")} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400" title="Reject"><X size={16} /></button>
                    </>
                  )}
                  {idea.status === "approved" && idea.channel_id && (
                    <button onClick={() => { setConvertingIdea(idea); setConvertDialogOpen(true); }} className="p-2 rounded-lg hover:bg-blue-500/10 text-gray-400 hover:text-blue-400" title="Convert to Video"><ArrowRight size={16} /></button>
                  )}
                  <button onClick={() => handleOpenEdit(idea)} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white"><Pencil size={16} /></button>
                  <button onClick={() => { setDeletingIdea(idea); setDeleteDialogOpen(true); }} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400"><Trash size={16} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredIdeas.length === 0 && (
        <div className="text-center py-16">
          <Lightbulb size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No ideas yet</h3>
          <p className="text-gray-400 mb-4">Start capturing your video ideas</p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="text-white">{editingIdea ? "Edit Idea" : "New Idea"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2"><Label className="text-gray-300">Title</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="bg-white/5 border-white/10 text-white" required data-testid="idea-title-input" /></div>
            <div className="space-y-2"><Label className="text-gray-300">Channel</Label>
              <Select value={formData.channel_id || "none"} onValueChange={(v) => setFormData({ ...formData, channel_id: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-[#1A1A2E] border-white/10">
                  <SelectItem value="none" className="text-gray-400">Unassigned</SelectItem>
                  {channels.map((c) => <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-300">Source</Label>
                <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">{SOURCES.map((s) => <SelectItem key={s} value={s} className="text-white capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label className="text-gray-300">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">{STATUSES.map((s) => <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label className="text-gray-300">Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-white/5 border-white/10 text-white min-h-[80px]" /></div>
            <div className="flex gap-3 pt-4"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 btn-secondary">Cancel</Button><Button type="submit" className="flex-1 btn-primary" data-testid="idea-submit-btn">{editingIdea ? "Save" : "Create"}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1A1A2E] border-white/10">
          <AlertDialogHeader><AlertDialogTitle className="text-white">Delete Idea?</AlertDialogTitle><AlertDialogDescription className="text-gray-400">This will permanently delete this idea.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <AlertDialogContent className="bg-[#1A1A2E] border-white/10">
          <AlertDialogHeader><AlertDialogTitle className="text-white">Convert to Video?</AlertDialogTitle><AlertDialogDescription className="text-gray-400">This will create a new video from "{convertingIdea?.title}" in the pipeline.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleConvert} className="bg-blue-600 hover:bg-blue-500 text-white">Convert</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
